// src/components/Navbar.js
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../api/axiosConfig';

const SidebarLink = ({ to, icon, label, onClick, isActiveOverride }) => {
    const location = useLocation();
    const basePath = to.split('?')[0];
    const isActive = isActiveOverride !== undefined 
        ? isActiveOverride 
        : (location.pathname === basePath || (basePath !== '/' && location.pathname.startsWith(basePath + '/')));
    
    return (
        <Link 
            to={to} 
            onClick={onClick}
            // Уменьшены отступы (py-2, gap-3) и увеличен контраст (text-slate-700)
            className={`flex items-center gap-3 px-4 py-2 rounded-xl font-bold transition-all ${
                isActive ? 'bg-blue-600 text-white shadow-md' : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700'
            }`}
        >
            <span className="text-xl shrink-0">{icon}</span> 
            <span className="truncate">{label}</span>
        </Link>
    );
};

const Navbar = () => {
    const { isAuthenticated, username, userData, logout, isAdmin, isTeacher, isStudent } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const isMainPage = location.pathname === '/';
    
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isBellOpen, setIsBellOpen] = useState(false);
    
    const [isQuestionsExpanded, setIsQuestionsExpanded] = useState(false);
    const [isClassesExpanded, setIsClassesExpanded] = useState(false);
    const [isTemplatesExpanded, setIsTemplatesExpanded] = useState(false);

    const [chats, setChats] = useState([]);
    const [myGroups, setMyGroups] = useState([]);
    const [myTemplates, setMyTemplates] = useState([]);

    const menuRef = useRef(null);
    const bellRef = useRef(null);

    useEffect(() => {
        if (isAuthenticated) {
            API.get('/api/chats/').then(res => setChats(res.data)).catch(() => {});
            const groupEndpoint = (isAdmin || isTeacher) ? '/api/teacher/groups/' : '/api/student/my-groups/';
            API.get(groupEndpoint).then(res => setMyGroups(res.data)).catch(() => {});
            if (isAdmin || isTeacher) {
                API.get('/api/test-templates/').then(res => setMyTemplates(res.data)).catch(() => {});
            }
        }
    }, [isAuthenticated, isAdmin, isTeacher]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            if (mobile) setIsSidebarOpen(false);
            else if (!isMainPage) setIsSidebarOpen(true);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMainPage]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) setIsProfileMenuOpen(false);
            if (bellRef.current && !bellRef.current.contains(event.target)) setIsBellOpen(false);
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (isMobile && isSidebarOpen) document.body.style.overflow = 'hidden';
        else document.body.style.overflow = ''; 
    }, [isMobile, isSidebarOpen]);

    const handleLogoutClick = () => {
        setIsProfileMenuOpen(false);
        setIsSidebarOpen(false);
        if (typeof logout === 'function') logout();
        navigate('/login');
    };

    const closeSidebar = () => setIsSidebarOpen(false);

    const unreadCount = chats.reduce((sum, chat) => sum + (chat.unread || 0), 0);
    const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const currentAvatar = userData?.avatar ? (userData.avatar.startsWith('http') ? userData.avatar : `${baseUrl}${userData.avatar}`) : null;

    const renderAvatarFallback = (size) => {
        const gender = userData?.gender || '';
        if (gender === 'male') return <span className="leading-none" style={{ fontSize: size === 'sm' ? '18px' : '32px' }}>👦</span>;
        if (gender === 'female') return <span className="leading-none" style={{ fontSize: size === 'sm' ? '18px' : '32px' }}>👧</span>;
        return <svg className={size === 'sm' ? "w-5 h-5 text-white/90 mt-1.5" : "w-10 h-10 text-white/90 mt-3"} viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>;
    };

    return (
        <>
            <nav className="bg-white border-b border-gray-100 sticky top-0 w-full z-[60] font-sans shadow-sm">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-3 md:gap-4">
                            {isAuthenticated && !isMainPage && (
                                <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="text-slate-500 hover:text-blue-600 hover:bg-blue-50 p-2 rounded-xl transition-colors focus:outline-none">
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                                </button>
                            )}
                            <Link to="/" className="flex items-center gap-2 group">
                                <div className="bg-blue-600 text-white font-black text-xl w-8 h-8 rounded-lg flex items-center justify-center shadow-md group-hover:bg-blue-700 transition-colors">ED</div>
                                <span className="font-extrabold text-xl tracking-tight text-slate-800 hidden sm:block">Платформа</span>
                            </Link>
                        </div>

                        <div className="flex items-center space-x-3 md:space-x-4">
                            {isAuthenticated ? (
                                <>
                                    <Link to="/calendar" className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors relative" title="Календарь">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                    </Link>

                                    <div className="relative" ref={bellRef}>
                                        <button onClick={() => setIsBellOpen(!isBellOpen)} className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors relative focus:outline-none" title="Сообщения">
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path></svg>
                                            {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>}
                                        </button>
                                        {isBellOpen && (
                                            <div className="absolute right-0 mt-2 w-72 md:w-80 bg-white rounded-2xl shadow-xl border border-slate-100 z-[70] overflow-hidden">
                                                <div className="px-4 py-3 border-b border-slate-50 bg-slate-50/50 flex justify-between items-center">
                                                    <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Сообщения</span>
                                                    <Link to="/messages" onClick={() => setIsBellOpen(false)} className="text-[10px] font-bold text-blue-600 hover:underline">Все чаты</Link>
                                                </div>
                                                <div className="max-h-72 overflow-y-auto custom-scrollbar">
                                                    {chats.length === 0 ? (
                                                        <div className="p-4 text-center text-xs text-slate-400 font-medium">Нет активных чатов</div>
                                                    ) : (
                                                        chats.slice(0, 5).map(chat => (
                                                            <Link key={chat.id} to="/messages" onClick={() => setIsBellOpen(false)} className="block p-3 border-b border-slate-50 hover:bg-blue-50 transition-colors">
                                                                <div className="flex justify-between items-start mb-1">
                                                                    <span className="font-bold text-xs text-slate-800 truncate pr-2">{chat.name}</span>
                                                                    {chat.unread > 0 && <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{chat.unread}</span>}
                                                                </div>
                                                                <p className="text-[11px] text-slate-500 truncate">{chat.lastMessage}</p>
                                                            </Link>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="relative pl-2 md:pl-4 md:border-l border-gray-200" ref={menuRef}>
                                        <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center space-x-2 bg-slate-50 hover:bg-slate-100 px-2 py-1.5 rounded-xl border border-slate-200 transition-all focus:outline-none">
                                            <div className="w-7 h-7 bg-blue-400 rounded-full flex items-center justify-center text-white shadow-sm overflow-hidden border border-blue-500 shrink-0">
                                                {currentAvatar ? <img src={currentAvatar} alt="Avatar" className="w-full h-full object-cover" /> : renderAvatarFallback('sm')}
                                            </div>
                                            <span className="text-slate-700 font-extrabold text-sm hidden md:block max-w-[100px] truncate">{username || 'Профиль'}</span>
                                        </button>

                                        {isProfileMenuOpen && (
                                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl py-2 border border-slate-100 z-[70]">
                                                <div className="px-4 py-3 border-b border-slate-50 mb-1">
                                                    <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Авторизован как:</p>
                                                    <p className="text-sm font-black text-slate-800 truncate">{username}</p>
                                                </div>
                                                <Link to="/profile" onClick={() => setIsProfileMenuOpen(false)} className="block px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors">Настройки профиля</Link>
                                                <button onClick={handleLogoutClick} className="block w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-bold mt-1 border-t border-slate-50">Выйти из системы</button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-2 md:gap-3">
                                    <Link to="/login" className="text-sm font-bold text-slate-700 hover:text-blue-700 transition-colors px-2">Войти</Link>
                                    <Link to="/register" className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-sm transition-colors">Регистрация</Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {!isMainPage && isAuthenticated && (
                <aside id="app-sidebar" className={`
                    fixed shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out bg-white
                    ${isMobile ? 'top-0 left-0 h-full w-72 z-[70]' : 'top-16 left-0 w-64 h-[calc(100vh-4rem)] border-r border-slate-200 z-[40]'}
                    ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                `}>
                    {isMobile && (
                        <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0">
                            <span className="font-black text-sm text-slate-400 uppercase tracking-widest">Навигация</span>
                            <button onClick={() => setIsSidebarOpen(false)} className="text-slate-400 hover:text-slate-800 bg-slate-50 hover:bg-slate-100 rounded-full p-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                    )}

                    <div className="flex flex-col p-4 space-y-0.5 overflow-y-auto flex-1 custom-scrollbar">
                        <SidebarLink to="/history" icon="📈" label="История" onClick={() => isMobile && setIsSidebarOpen(false)} />
                        
                        {(isAdmin || isTeacher) && (
                            <>
                                <div className="my-2 border-t border-slate-100"></div>

                                {/* АККОРДЕОН: БАЗА ВОПРОСОВ */}
                                <div>
                                    <button onClick={() => setIsQuestionsExpanded(!isQuestionsExpanded)} className="w-full flex items-center justify-between px-4 py-2 rounded-xl font-bold text-slate-700 hover:bg-slate-50 hover:text-blue-700 transition-colors">
                                        <div className="flex items-center gap-3"><span className="text-xl shrink-0">📚</span><span>База вопросов</span></div>
                                        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isQuestionsExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-300 ${isQuestionsExpanded ? 'max-h-[300px]' : 'max-h-0'}`}>
                                        <div className="pl-11 pr-2 py-1 flex flex-col overflow-y-auto custom-scrollbar">
                                            <Link to="/questions?action=summary" onClick={() => isMobile && setIsSidebarOpen(false)} className={`block py-1.5 text-xs font-bold px-3 rounded-lg transition-colors ${location.search.includes('action=summary') ? 'bg-blue-50 text-blue-700' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}>
                                                📊 Сводка платформы
                                            </Link>
                                            <Link to="/questions" onClick={() => isMobile && setIsSidebarOpen(false)} className={`block py-1.5 text-xs font-bold px-3 rounded-lg truncate transition-colors ${location.pathname === '/questions' && !location.search.includes('mode=') && !location.search.includes('action=summary') ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'}`}>
                                                Все вопросы
                                            </Link>
                                            <Link to="/questions?action=add&mode=manual" onClick={() => isMobile && setIsSidebarOpen(false)} className={`block py-1.5 text-xs font-bold px-3 rounded-lg transition-colors ${location.search.includes('mode=manual') ? 'bg-blue-50 text-blue-700' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}>
                                                ✍️ Ручная форма
                                            </Link>
                                            <Link to="/questions?action=add&mode=excel" onClick={() => isMobile && setIsSidebarOpen(false)} className={`block py-1.5 text-xs font-bold px-3 rounded-lg transition-colors ${location.search.includes('mode=excel') ? 'bg-blue-50 text-blue-700' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}>
                                                📥 Добавить Экзель
                                            </Link>
                                            <Link to="/questions?action=add&mode=ai" onClick={() => isMobile && setIsSidebarOpen(false)} className={`block py-1.5 text-xs font-bold px-3 rounded-lg transition-colors ${location.search.includes('mode=ai') ? 'bg-blue-50 text-blue-700' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}>
                                                📸 Добавить по фото
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <div className="my-2 border-t border-slate-100"></div>

                                {/* АККОРДЕОН: МОИ КЛАССЫ */}
                                <div>
                                    <button onClick={() => setIsClassesExpanded(!isClassesExpanded)} className="w-full flex items-center justify-between px-4 py-2 rounded-xl font-bold text-slate-700 hover:bg-slate-50 hover:text-blue-700 transition-colors">
                                        <div className="flex items-center gap-3"><span className="text-xl shrink-0">👥</span><span>Мои классы</span></div>
                                        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isClassesExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-300 ${isClassesExpanded ? 'max-h-64' : 'max-h-0'}`}>
                                        <div className="pl-11 pr-2 py-1 flex flex-col overflow-y-auto custom-scrollbar max-h-60">
                                            <Link to="/teacher-dashboard?action=new-group" onClick={() => isMobile && setIsSidebarOpen(false)} className="block py-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 rounded-lg transition-colors">
                                                + Создать группу
                                            </Link>
                                            {myGroups.map(group => (
                                                <Link key={group.id} to={`/teacher-dashboard?groupId=${group.id}`} onClick={() => isMobile && setIsSidebarOpen(false)} className="block py-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 px-3 rounded-lg truncate transition-colors">
                                                    {group.name}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* АККОРДЕОН: МОИ ШАБЛОНЫ */}
                                <div>
                                    <button onClick={() => setIsTemplatesExpanded(!isTemplatesExpanded)} className="w-full flex items-center justify-between px-4 py-2 rounded-xl font-bold text-slate-700 hover:bg-slate-50 hover:text-blue-700 transition-colors">
                                        <div className="flex items-center gap-3"><span className="text-xl shrink-0">📋</span><span>Мои шаблоны</span></div>
                                        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isTemplatesExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-300 ${isTemplatesExpanded ? 'max-h-64' : 'max-h-0'}`}>
                                        <div className="pl-11 pr-2 py-1 flex flex-col overflow-y-auto custom-scrollbar max-h-60">
                                            {myTemplates.map(tpl => (
                                                <Link key={tpl.id} to={`/teacher-dashboard?templateId=${tpl.id}`} onClick={() => isMobile && setIsSidebarOpen(false)} className="block py-1.5 text-xs font-bold text-slate-700 hover:text-slate-900 hover:bg-slate-100 px-3 rounded-lg truncate transition-colors">
                                                    {tpl.title}
                                                </Link>
                                            ))}
                                            <Link to="/teacher/constructor" onClick={() => isMobile && setIsSidebarOpen(false)} className="block py-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 rounded-lg transition-colors">
                                                + Создать шаблон
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {isStudent && (
                            <>
                                <div className="my-2 border-t border-slate-100"></div>
                                <SidebarLink to="/student-dashboard" icon="👥" label="Мои классы" onClick={() => isMobile && setIsSidebarOpen(false)} />
                            </>
                        )}

                        {isAdmin && (
                            <>
                                <div className="my-3 border-t border-slate-100"></div>
                                <div className="px-4 py-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">Админ-панель</div>
                                <SidebarLink to="/analytics?tab=users" icon="👥" label="Пользователи" onClick={() => isMobile && setIsSidebarOpen(false)} isActiveOverride={location.pathname === '/analytics' && (!location.search || location.search.includes('tab=users'))} />
                                <SidebarLink to="/analytics?tab=questions" icon="📚" label="Банк вопросов" onClick={() => isMobile && setIsSidebarOpen(false)} isActiveOverride={location.pathname === '/analytics' && location.search.includes('tab=questions')} />
                            </>
                        )}
                    </div>

                    {isMobile && (
                        <div className="p-4 border-t border-slate-100 shrink-0">
                            <button onClick={handleLogoutClick} className="w-full flex justify-center bg-red-50 hover:bg-red-100 text-red-600 font-bold py-3 rounded-xl transition-colors">Выйти</button>
                        </div>
                    )}
                </aside>
            )}

            {isMobile && isSidebarOpen && !isMainPage && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[65] transition-opacity" onClick={() => setIsSidebarOpen(false)}></div>
            )}

            {!isMobile && !isMainPage && (
                <style>{`
                    #app-sidebar ~ * {
                        margin-left: ${isSidebarOpen ? '16rem' : '0'} !important;
                        transition: margin-left 0.3s ease-in-out;
                    }
                `}</style>
            )}
        </>
    );
};

export default Navbar;